import { Alert, Box } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  CertificateTemplateRequest,
  ResponseOfCertificateTemplate,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import CertificateTemplateForm from "./CertificateTemplateForm";
import CertificateTemplateProfessionsOverride from "./CertificateTemplateProfessionsOverride";

const CertificateTemplateUpdateForm = () => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    certificateService,
    certificateTemplatesUrl,
    certificateTemplateDetailsUrl,
  } = useGlobalContext();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const certificateTemplateResult = useQuery<ResponseOfCertificateTemplate>(
    ["certificateTemplate", id],
    () => certificateService.getCertificateTemplate(id!),
    {
      enabled: !!id,
    }
  );

  const certificateTemplate = certificateTemplateResult.data?.data;
  useTitle(
    certificateTemplate?.name ||
      t({ id: "certificateTemplateFormFormUpdateButton" })
  );

  const redirectToDetails = () => {
    navigate(certificateTemplateDetailsUrl(id));
  };

  const onSubmit = async (values: CertificateTemplateRequest) => {
    try {
      values.id = id;
      var result = await registerPromise(
        certificateService.saveCertificateTemplate(values)
      );
      queryClient.invalidateQueries("certificateTemplates");
      queryClient.invalidateQueries(["certificateTemplate", id]);
      queryClient.invalidateQueries(["certificaTemplatePreview", id]);
      queryClient.invalidateQueries([
        "certificateTemplatesOverridesPreview",
        id,
      ]);
      redirectToDetails();
      return result;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  if (!id) return <Navigate to={certificateTemplatesUrl()} replace />;
  if (certificateTemplateResult.isLoading) return <Loader />;
  if (certificateTemplateResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="certificateTemplateLoadError" />
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={certificateTemplateDetailsUrl(id)}
        labelKey="backToDetailsButtonLabel"
      />
      <h2>
        <T id="certificateTemplateFormEditTitle" />
      </h2>
      <CertificateTemplateForm
        entity={certificateTemplate}
        onSubmit={onSubmit}
        submitLabel={t({ id: "certificateTemplateFormFormUpdateButton" })}
        onCancel={redirectToDetails}
      />
      <CertificateTemplateProfessionsOverride certificateTemplateId={id} />
    </>
  );
};

export default CertificateTemplateUpdateForm;
